import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {MapPin, Search, Plus, Edit, Trash2, Box, AlertTriangle, AlertCircle, CheckCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Shield, Layers, Gauge, FileText, Clock, List, LayoutList, Check, PauseCircle, Package, Warehouse, Grid3x3, ChevronRight, GitBranch, Tag} from 'lucide-react';
import InventaireListPageShell from './components/InventaireListPageShell';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { InventaireModalPortal, InventaireViewModalPortal } from './components/inventaireModalPortal';
import FacturationFormModalTabs from '../facturation/components/FacturationFormModalTabs';
import {
  sliceQawaemStats,
  buildInventaireBulkDeleteFilterAction,
  InventaireTableSelectAllHeader,
  InventaireTableRowCheckbox,
  INVENTAIRE_TABLE_ACTIONS_TH_CLASS,
  buildInventaireImportExportProps,
  buildInventaireColumnVisibilityState,
  buildInventaireQawaemColumnDefs,
  isInventaireColumnVisible,
  inventaireTableColSpan,
  InventoryConfigNotice,
  useInventaireListSettings,
  sliceInventaireListItems,
  inventaireRecordInDateRange,
  INVENTAIRE_FORM_GRID,
  fetchInventaireWarehouses,
  buildInventaireWarehouseSelectOptions,
  mapInventaireWarehouseSelection,
} from './inventaireListPageUtils';
import { useInventoryConfig } from './useInventoryConfig';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { useTranslation } from 'react-i18next';

const EmplacementsZonesPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.inventaire.pages.emplacementsZonesPage.${key}`, opts);
  const inventoryConfig = useInventoryConfig();
  const { dateRange, viewMode, setViewMode, itemsPerPage } = useInventaireListSettings(inventoryConfig);
  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Status dropdown
      const isClickOnStatusButton = event.target.closest('[data-location-status-dropdown-button]');
      const isClickInStatusDropdown = event.target.closest('[data-location-status-dropdown]');
      if (!isClickOnStatusButton && !isClickInStatusDropdown) {
        setShowStatusDropdown(false);
      }
      
      // Zone type dropdown
      const isClickOnZoneTypeButton = event.target.closest('[data-location-zonetype-dropdown-button]');
      const isClickInZoneTypeDropdown = event.target.closest('[data-location-zonetype-dropdown]');
      if (!isClickOnZoneTypeButton && !isClickInZoneTypeDropdown) {
        setShowZoneTypeDropdown(false);
      }
      
      // Location type dropdown
      const isClickOnLocationTypeButton = event.target.closest('[data-location-locationtype-dropdown-button]');
      const isClickInLocationTypeDropdown = event.target.closest('[data-location-locationtype-dropdown]');
      if (!isClickOnLocationTypeButton && !isClickInLocationTypeDropdown) {
        setShowLocationTypeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [locations, setLocations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  // Collapsible sections state
  const [locationFormTab, setLocationFormTab] = useState('basicInfo');

  const locationFormTabs = useMemo(() => ([
    { id: 'basicInfo', label: p('section_basic_info'), icon: MapPin, iconColor: 'text-emerald-400' },
    { id: 'capacityInfo', label: p('section_capacity'), icon: Box, iconColor: 'text-yellow-400' },
  ]), [p]);

  
  const [formData, setFormData] = useState({ 
    location_name: '', 
    warehouse_id: '', 
    zone_type: 'picking',
    location_type: 'bin',
    parent_location_id: '',
    capacity_volume: '',
    capacity_weight: '',
    current_occupancy: '',
    status: 'available',
    temperature_controlled: false,
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showZoneTypeDropdown, setShowZoneTypeDropdown] = useState(false);
  const [showLocationTypeDropdown, setShowLocationTypeDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_locations');
  const [quickFilter, setQuickFilter] = useState('all');
  
  // PDF and Excel export states
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const zoneTypeDropdownRef = useRef(null);
  const locationTypeDropdownRef = useRef(null);

  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const getLocationTypeLabel = (type) => {
    switch (type) {
      case 'zone': return p('type_zone');
      case 'aisle': return p('type_aisle');
      case 'shelf': return p('type_shelf');
      case 'bin': return p('type_bin');
      default: return type;
    }
  };

  // Multi-column sorting state

  useEffect(() => { 
    loadLocations();
    loadWarehouses();
  }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) setShowStatusDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadLocations = async () => {
    try {
      const response = await localApiAdapter.get('/inventory/locations');
      setLocations(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading locations:', error);
      setLocations([]);
    }
  };

  const loadWarehouses = async () => {
    try {
      setWarehouses(await fetchInventaireWarehouses());
    } catch (error) {
      console.error('Error loading warehouses:', error);
      setWarehouses([]);
    }
  };

  const stats = {
    total: locations.length,
    available: locations.filter(l => l.status === 'available').length,
    occupied: locations.filter(l => l.status === 'occupied').length,
    blocked: locations.filter(l => l.status === 'blocked').length,
    totalCapacity: locations.reduce((sum, l) => sum + (parseFloat(l.capacity_volume) || 0), 0),
    currentOccupancy: locations.reduce((sum, l) => sum + (parseFloat(l.current_occupancy) || 0), 0)
  };

  const mainTabs = [
    { id: 'all_locations', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_type', name: p('tab_by_type'), icon: Grid3x3 },
    { id: 'par_zone', name: p('tab_by_zone'), icon: Layers }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'available', name: p('filter_available'), icon: CheckCircle },
    { id: 'occupied', name: p('filter_occupied'), icon: Package },
    { id: 'blocked', name: p('filter_blocked'), icon: AlertTriangle }
  ];

  const locationDistribution = {
    all_locations: {
      title: p('overview_title'),
      data: [
        { filterKey: 'available', name: p('dist_available'), count: stats.available, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'occupied', name: p('dist_occupied'), count: stats.occupied, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'blocked', name: p('dist_blocked'), count: stats.blocked, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    },
    par_type: {
      title: p('distribution_by_type'),
      data: [
        { filterKey: 'bin', name: p('dist_bins'), count: locations.filter(l => l.location_type === 'bin').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'shelf', name: p('dist_shelves'), count: locations.filter(l => l.location_type === 'shelf').length, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
        { filterKey: 'aisle', name: p('dist_aisles'), count: locations.filter(l => l.location_type === 'aisle').length, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    },
    par_zone: {
      title: p('distribution_by_zone'),
      data: [
        { filterKey: 'picking', name: p('dist_picking'), count: locations.filter(l => l.zone_type === 'picking').length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'bulk', name: p('dist_bulk'), count: locations.filter(l => l.zone_type === 'bulk').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'staging', name: p('dist_staging'), count: locations.filter(l => l.zone_type === 'staging').length, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" }
      ]
    }
  };

  const distributionInfo = locationDistribution[mainViewMode] || locationDistribution.all_locations;

  const baseFilteredLocations = locations.filter(l => {
    if (!inventaireRecordInDateRange(dateRange, l.created_at)) return false;

    if (searchTerm && !l.location_code?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !l.location_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (mainViewMode === 'all_locations') {
      if (quickFilter === 'available' && l.status !== 'available') return false;
      if (quickFilter === 'occupied' && l.status !== 'occupied') return false;
      if (quickFilter === 'blocked' && l.status !== 'blocked') return false;
    }
    
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_locations':
          if (activeDistributionFilter === 'available' && l.status !== 'available') return false;
          if (activeDistributionFilter === 'occupied' && l.status !== 'occupied') return false;
          if (activeDistributionFilter === 'blocked' && l.status !== 'blocked') return false;
          break;
        case 'par_type':
          if (activeDistributionFilter === 'bin' && l.location_type !== 'bin') return false;
          if (activeDistributionFilter === 'shelf' && l.location_type !== 'shelf') return false;
          if (activeDistributionFilter === 'aisle' && l.location_type !== 'aisle') return false;
          break;
        case 'par_zone':
          if (activeDistributionFilter === 'picking' && l.zone_type !== 'picking') return false;
          if (activeDistributionFilter === 'bulk' && l.zone_type !== 'bulk') return false;
          if (activeDistributionFilter === 'staging' && l.zone_type !== 'staging') return false;
          break;
      }
    }
    
    return true;
  });

  const columnFilterRules = useMemo(() => [
    textColumnRule('location_code'),
    textColumnRule('location_name'),
    textColumnRule('warehouse_name'),
    exactColumnRule('location_type', (r) => r.location_type ?? r.type),
    textColumnRule('capacity_volume', (r) => String(r.capacity_volume ?? '')),
    textColumnRule('current_occupancy', (r) => String(r.current_occupancy ?? '')),
    exactColumnRule('status', (r) => r.status),
  ], []);

  const filteredLocations = useMemo(
    () => applyColumnFilters(baseFilteredLocations, colFilters, columnFilterRules),
    [baseFilteredLocations, colFilters, columnFilterRules],
  );
  const visibleLocations = useMemo(
    () => sliceInventaireListItems(filteredLocations, itemsPerPage),
    [filteredLocations, itemsPerPage],
  );

  const openNewLocationModal = () => {
    if (!inventoryConfig.enableWarehouses) {
      window.alert(p('notice_warehouses_disabled', { defaultValue: 'إدارة المستودعات معطّلة من إعدادات المخزون.' }));
      return;
    }
    setEditingRecord(null);
    setLocationFormTab('basicInfo');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { 
        location_name: formData.location_name,
        warehouse_id: formData.warehouse_id || null,
        zone_type: formData.zone_type,
        location_type: formData.location_type,
        parent_location_id: parseInt(formData.parent_location_id) || null,
        capacity_volume: parseFloat(formData.capacity_volume) || 0,
        capacity_weight: parseFloat(formData.capacity_weight) || 0,
        current_occupancy: parseFloat(formData.current_occupancy) || 0,
        status: formData.status,
        temperature_controlled: formData.temperature_controlled || false,
        notes: formData.notes || ''
      };
      
      if (editingRecord) {
        await localApiAdapter.put(`/inventory/locations/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'الموقع' }));
      } else {
        await localApiAdapter.post('/inventory/locations', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'الموقع' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      return;
    } finally {
      resetForm();
      loadLocations();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ 
      location_name: record.location_name || '',
      warehouse_id: record.warehouse_id?.toString() || '',
      zone_type: record.zone_type || 'picking',
      location_type: record.location_type || 'bin',
      parent_location_id: record.parent_location_id?.toString() || '',
      capacity_volume: record.capacity_volume?.toString() || '',
      capacity_weight: record.capacity_weight?.toString() || '',
      current_occupancy: record.current_occupancy?.toString() || '',
      status: record.status || 'available',
      temperature_controlled: record.temperature_controlled || false,
      notes: record.notes || ''
    });
    setLocationFormTab('basicInfo');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/inventory/locations/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'الموقع' }));
        loadLocations();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      }
    }
  };

  const filteredLocationsIds = useMemo(() => filteredLocations.map((r) => r.id), [filteredLocations]);
  const allRowsSelected = filteredLocationsIds.length > 0 && filteredLocationsIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredLocationsIds);
    }
  };
  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };
  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/inventory/locations/${id}`)));
        notifyBulkDeleted(showNotify, selectedRecords.length, p('entity_label', { defaultValue: 'الموقع' }));
        setSelectedRecords([]);
        loadLocations();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      location_name: '', 
      warehouse_id: '', 
      zone_type: 'picking',
      location_type: 'bin',
      parent_location_id: '',
      capacity_volume: '',
      capacity_weight: '',
      current_occupancy: '',
      status: 'available',
      temperature_controlled: false,
      notes: ''
    });
    setEditingRecord(null);
    setLocationFormTab('basicInfo');
    setShowModal(false);
  };


  // Export handlers
  const handleExport = () => {
    exportToCSV(filteredLocations, 'emplacements_zones', [
      { key: 'location_code', label: p('export_col_code') },
      { key: 'location_name', label: p('export_col_name') },
      { key: 'zone_type', label: p('export_col_zone_type') },
      { key: 'location_type', label: p('export_col_location_type') },
      { key: 'capacity_volume', label: p('export_col_capacity_volume') },
      { key: 'capacity_weight', label: p('export_col_capacity_weight') },
      { key: 'current_occupancy', label: p('export_col_occupancy') },
      { key: 'status', label: p('export_col_status') }
    ]);
  };

  // Export PDF
  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      
      const filteredData = filteredLocations;
      
      // Create a temporary table element for PDF generation
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('export_col_code')}</th>
                <th>${p('export_col_name')}</th>
                <th>${p('export_col_type')}</th>
                <th>${p('export_col_zone')}</th>
                <th>${p('export_col_capacity')}</th>
                <th>${p('export_col_occupancy')}</th>
                <th>${p('export_col_status')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.location_code || p('na')}</td>
                  <td>${item.location_name || p('na')}</td>
                  <td>${getLocationTypeLabel(item.location_type)}</td>
                  <td>${item.zone_type || p('na')}</td>
                  <td>${item.capacity_volume || 0} m³</td>
                  <td>${item.current_occupancy || 0}%</td>
                  <td>${item.status || p('na')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      
      document.body.appendChild(tempDiv);
      
      await generateReportPDF(p('report_pdf_title'), {});
      
      document.body.removeChild(tempDiv);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(p('alert_pdf_error'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Export Excel (Advanced)
  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      
      const filteredData = filteredLocations;
      
      const columns = [
        { key: 'location_code', label: p('export_col_code'), width: 15 },
        { key: 'location_name', label: p('export_col_name'), width: 25 },
        { key: 'zone_type', label: p('export_col_zone_type'), width: 15 },
        { key: 'location_type', label: p('export_col_location_type'), width: 15 },
        { key: 'capacity_volume', label: p('export_col_capacity_volume_m3'), width: 15 },
        { key: 'capacity_weight', label: p('export_col_capacity_weight_kg'), width: 15 },
        { key: 'current_occupancy', label: p('export_col_occupancy_pct'), width: 12 },
        { key: 'temperature_controlled', label: p('export_col_temp_controlled'), width: 18 },
        { key: 'status', label: p('export_col_status'), width: 12 }
      ];
      
      const data = filteredData.map(item => ({
        location_code: item.location_code || '',
        location_name: item.location_name || '',
        zone_type: item.zone_type || '',
        location_type: getLocationTypeLabel(item.location_type),
        capacity_volume: item.capacity_volume || 0,
        capacity_weight: item.capacity_weight || 0,
        current_occupancy: item.current_occupancy || 0,
        temperature_controlled: item.temperature_controlled ? p('yes') : p('no'),
        status: item.status || ''
      }));
      
      await generateReportExcel('locations_report', p('report_excel_title'), {
        sheetName: p('report_excel_sheet'),
      });
      
    } catch (error) {
      console.error('Error generating Excel:', error);
      alert(p('alert_excel_error'));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const statusOptions = [
    { value: 'available', label: p('status_available'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'occupied', label: p('status_occupied'), icon: Package, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'blocked', label: p('status_blocked'), icon: AlertTriangle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    { value: 'maintenance', label: p('status_maintenance'), icon: PauseCircle, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' }
  ];

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/inventory/locations/${recordId}`, { status: newStatus });
      await loadLocations();
    } catch (error) {
      console.error('❌ Error updating status:', error);
    }
  };

  const TreeView = ({
    locations: treeLocations,
    warehouses: treeWarehouses,
    expandedNodes,
    setExpandedNodes,
    onEdit,
    onDelete,
    onView,
  }) => {
    const toggleNode = (nodeId) => {
      setExpandedNodes((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }));
    };

    const childrenByParent = useMemo(() => {
      const map = new Map();
      treeLocations.forEach((loc) => {
        const parentKey = loc.parent_location_id ? String(loc.parent_location_id) : 'root';
        if (!map.has(parentKey)) map.set(parentKey, []);
        map.get(parentKey).push(loc);
      });
      return map;
    }, [treeLocations]);

    const renderLocationNode = (node, depth = 0) => {
      const nodeKey = `loc-${node.id}`;
      const childNodes = childrenByParent.get(String(node.id)) || [];
      const hasChildren = childNodes.length > 0;
      const isExpanded = expandedNodes[nodeKey];

      return (
        <div key={node.id} className="border-l border-white/10 ml-3">
          <div
            className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-white/5"
            style={{ paddingLeft: `${depth * 16 + 12}px` }}
          >
            {hasChildren ? (
              <button type="button" onClick={() => toggleNode(nodeKey)} className="text-white/60 hover:text-white">
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
            ) : (
              <span className="w-4" />
            )}
            <MapPin size={16} className="text-emerald-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{node.location_code}</p>
              <p className="text-white/50 text-xs truncate">{node.location_name}</p>
            </div>
            <QawaemClickableStatusBadge
              status={node.status}
              recordId={node.id}
              options={statusOptions}
              onChange={handleStatusChange}
              title={p('click_change_status') || t('common.change_status')}
            />
                      <QawaemErpRowActions
                        compact
                        size={14}
              onView={() => onView(node)}
              onEdit={() => onEdit(node)}
              onDelete={() => onDelete(node.id)}
              viewTitle={p('action_view') || t('common.view')}
              editTitle={t('common.edit')}
              deleteTitle={t('common.delete')}
            />
          </div>
          {hasChildren && isExpanded ? childNodes.map((child) => renderLocationNode(child, depth + 1)) : null}
        </div>
      );
    };

    const grouped = useMemo(() => {
      const byWarehouse = new Map();
      treeWarehouses.forEach((wh) => {
        byWarehouse.set(String(wh.id), { warehouse: wh, roots: [] });
      });
      treeLocations.forEach((loc) => {
        const whKey = String(loc.warehouse_id || 'unknown');
        if (!byWarehouse.has(whKey)) {
          byWarehouse.set(whKey, { warehouse: { id: whKey, name: loc.warehouse_name || p('na') }, roots: [] });
        }
        if (!loc.parent_location_id) {
          byWarehouse.get(whKey).roots.push(loc);
        }
      });
      return [...byWarehouse.values()].filter((g) => g.roots.length > 0);
    }, [treeLocations, treeWarehouses, p]);

    if (grouped.length === 0) {
      return (
        <div className="text-center py-12 text-white/60">
          <GitBranch size={48} className="mx-auto mb-4 text-white/30" />
          <p>{p('empty')}</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {grouped.map(({ warehouse, roots }) => {
          const whKey = `wh-${warehouse.id}`;
          const isExpanded = expandedNodes[whKey] !== false;
          return (
            <div key={warehouse.id} className="bg-white/[0.03] rounded-2xl border border-white/20 overflow-hidden">
              <button
                type="button"
                onClick={() => toggleNode(whKey)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-left"
              >
                {isExpanded ? <ChevronDown size={18} className="text-white/60" /> : <ChevronRight size={18} className="text-white/60" />}
                <Warehouse size={18} className="text-blue-400" />
                <span className="text-white font-semibold">{warehouse.name}</span>
                <span className="text-white/40 text-sm">({roots.length})</span>
              </button>
              {isExpanded ? (
                <div className="px-2 pb-3">
                  {roots.map((root) => renderLocationNode(root, 0))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };


  const INVENTAIRE_TABLE_COLUMNS = [
    { key: 'location_code', labelKey: 'col_code' },
    { key: 'location_name', labelKey: 'col_name' },
    { key: 'warehouse_name', labelKey: 'col_warehouse' },
    { key: 'location_type', labelKey: 'col_type' },
    { key: 'capacity_volume', labelKey: 'col_capacity' },
    { key: 'current_occupancy', labelKey: 'col_occupancy' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', labelKey: 'col_actions', required: true },
  ];

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildInventaireColumnVisibilityState(INVENTAIRE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildInventaireQawaemColumnDefs(INVENTAIRE_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isInventaireColumnVisible(columnVisibility, key);
  const tableColSpan = inventaireTableColSpan(columnVisibility);

  const inventaireStatEntries = useMemo(() => sliceQawaemStats([
    { key: 'stat_total', label: p('stat_total'), value: stats.total, icon: MapPin },
    { key: 'stat_available', label: p('stat_available'), value: stats.available, icon: CheckCircle },
    { key: 'stat_occupied', label: p('stat_occupied'), value: stats.occupied, icon: Package },
    { key: 'stat_blocked', label: p('stat_blocked'), value: stats.blocked, icon: XCircle },
  ], 4), [stats, p]);


  const handleImport = () => {
    triggerFileInput((file) => {
      importFromFile(file, () => loadLocations());
    });
  };

  const importExportProps = useMemo(() => buildInventaireImportExportProps({
    showImportDropdown,
    setShowImportDropdown,
    showExportDropdown,
    setShowExportDropdown,
    importDropdownRef,
    exportDropdownRef,
    importLabel: t('common.import'),
    exportLabel: t('common.export'),
    generatingLabel: p('generating'),
    isGeneratingPDF,
    handlers: {
      onImport: handleImport,
      onExportCSV: handleExport,
      onExportPDF: handleExportPDF,
      onExportExcel: handleExportExcel,
    },
  }), [showImportDropdown, showExportDropdown, isGeneratingPDF, t, p]);

  return (
    <>
    <InventoryConfigNotice
      message={!inventoryConfig.enableWarehouses ? p('notice_warehouses_disabled_banner', { defaultValue: 'إدارة المستودعات معطّلة — فعّلها من إعدادات المخزون ← المستودعات.' }) : null}
    />
    <InventaireListPageShell
      title={p('title')}
      subtitle={p('subtitle')}
      icon={MapPin}
      statEntries={inventaireStatEntries}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      onCreate={openNewLocationModal}
      createLabel={p('new_location')}
      importExport={importExportProps}
      filterActions={buildInventaireBulkDeleteFilterAction({ count: selectedRecords.length, onDelete: handleBulkDelete, p })}
    >
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <InventaireTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onSelectAll={handleSelectAll} p={p} />
                  
                  {isColVisible('location_code') && (

                  
                    <TableColumnHeaderFilter columnKey="location_code" label={p('col_code')} value={colFilters.location_code ?? ''} onChange={(v) => setColFilter('location_code', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('location_name') && (

                    <TableColumnHeaderFilter columnKey="location_name" label={p('col_name')} value={colFilters.location_name ?? ''} onChange={(v) => setColFilter('location_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('warehouse_name') && (

                    <TableColumnHeaderFilter columnKey="warehouse_name" label={p('col_warehouse')} value={colFilters.warehouse_name ?? ''} onChange={(v) => setColFilter('warehouse_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('location_type') && (

                    <TableColumnHeaderFilter columnKey="location_type" label={p('col_type')} value={colFilters.location_type ?? ''} onChange={(v) => setColFilter('location_type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('capacity_volume') && (

                    <TableColumnHeaderFilter columnKey="capacity_volume" label={p('col_capacity')} value={colFilters.capacity_volume ?? ''} onChange={(v) => setColFilter('capacity_volume', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('current_occupancy') && (

                    <TableColumnHeaderFilter columnKey="current_occupancy" label={p('col_occupancy')} value={colFilters.current_occupancy ?? ''} onChange={(v) => setColFilter('current_occupancy', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('status') && (

                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusOptions.map((s) => ({ value: s.value, label: s.label }))} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName={INVENTAIRE_TABLE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {visibleLocations.length > 0 ? (
                  visibleLocations.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <InventaireTableRowCheckbox checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                      
                      {isColVisible('location_code') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.location_code}</span>
                          <span className="text-white/50 text-xs mt-0.5">{p('unique_code')}</span>
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('location_name') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.location_name}</span>
                          <span className="text-white/50 text-xs mt-0.5">{record.zone_type || p('na')}</span>
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('warehouse_name') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">
                            {warehouses.find(w => w.id === record.warehouse_id)?.name || p('na')}
                          </span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {warehouses.find(w => w.id === record.warehouse_id)?.code || '—'}
                          </span>
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('location_type') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{getLocationTypeLabel(record.location_type)}</span>
                          <span className="text-white/50 text-xs mt-0.5">{record.temperature_controlled ? p('temperature_controlled') : p('standard')}</span>
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('capacity_volume') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.capacity_volume || 0} m³</span>
                          <span className="text-white/50 text-xs mt-0.5">{record.capacity_weight || 0} kg</span>
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('current_occupancy') && (

                      
                        <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.current_occupancy || 0}%</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {record.current_occupancy >= 90 ? p('almost_full') : record.current_occupancy >= 50 ? p('medium') : p('occupancy_free')}
                          </span>
                        </div>
                      </td>

                      
                      )}
                      
                      {isColVisible('status') && (

                      
                        <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div></td>

                      
                      )}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><MapPin size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'tree' && (
          <div className="p-6">
            <TreeView 
              locations={filteredLocations}
              warehouses={warehouses}
              expandedNodes={expandedNodes}
              setExpandedNodes={setExpandedNodes}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onView={(record) => { setViewingRecord(record); setShowViewModal(true); }}
            />
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {visibleLocations.length > 0 ? (
              visibleLocations.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <MapPin size={20} className="text-emerald-400" />
                          {record.location_code}
                        </h3>
                        <div className="flex"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                          {record.location_name}
                        </span>
                      </div>
                      
                      <div className={INVENTAIRE_FORM_GRID}>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_basic_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_code')}:</span> {record.location_code}</div>
                            <div><span className="font-medium">{p('label_name')}:</span> {record.location_name}</div>
                            <div><span className="font-medium">{p('label_type')}:</span> {getLocationTypeLabel(record.location_type)}</div>
                            <div><span className="font-medium">{p('label_zone')}:</span> {record.zone_type || p('na')}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_capacity')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_volume')}:</span> {record.capacity_volume || 0} m³</div>
                            <div><span className="font-medium">{p('label_weight')}:</span> {record.capacity_weight || 0} kg</div>
                            <div><span className="font-medium">{p('label_occupancy')}:</span> {record.current_occupancy || 0}%</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_characteristics')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_temperature')}:</span> {record.temperature_controlled ? p('temperature_controlled_short') : p('standard')}</div>
                            <div><span className="font-medium">{p('label_warehouse')}:</span> {warehouses.find(w => w.id === record.warehouse_id)?.name || p('na')}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_notes')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <p>{record.notes || p('no_notes')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <MapPin size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
    </InventaireListPageShell>

      {/* View Modal */}
      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => setShowViewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_title')}</h2>
                <button onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <MapPin size={18} className="text-emerald-400" />
                    {p('section_basic_info')}
                  </h3>
                  <div className={INVENTAIRE_FORM_GRID}>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_code')}</p>
                      <p className="text-white font-medium text-lg">{viewingRecord.location_code}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_name')}</p>
                      <p className="text-white font-medium">{viewingRecord.location_name}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_type')}</p>
                      <p className="text-white font-medium">{getLocationTypeLabel(viewingRecord.location_type)}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('col_status')}</p>
                      <div className="flex"><QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status') || t('common.change_status')} /></div>
                    </div>
                  </div>
                </div>

                <div className={INVENTAIRE_FORM_GRID}>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Box size={18} className="text-blue-400" />
                      {p('section_capacity')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_volume')}</p>
                        <p className="text-white font-medium">{viewingRecord.capacity_volume || 0} m³</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_weight')}</p>
                        <p className="text-white font-medium">{viewingRecord.capacity_weight || 0} kg</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_current_occupancy')}</p>
                        <p className="text-white font-medium">{viewingRecord.current_occupancy || 0}%</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Warehouse size={18} className="text-purple-400" />
                      {p('section_zone_warehouse')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_zone_type')}</p>
                        <p className="text-white font-medium">{viewingRecord.zone_type || p('na')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_warehouse')}</p>
                        <p className="text-white font-medium">{viewingRecord.warehouse_id || p('na')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_temperature_controlled')}</p>
                        <p className="text-white font-medium">{viewingRecord.temperature_controlled ? p('yes') : p('no')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <FileText size={18} className="text-yellow-400" />
                    {p('section_notes')}
                  </h3>
                  <p className="text-white/80">{viewingRecord.notes || p('no_notes')}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Edit/Create Modal */}
      {showModal && (
        <InventaireModalPortal show={showModal} onClose={resetForm} maxWidth="1200px">
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
              <h2 className="text-2xl font-bold text-white">{editingRecord ? p('edit_location') : p('create_location')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="location-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <Check size={18} />
                  {editingRecord ? p('update') : p('create')}
                </button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            
            <FacturationFormModalTabs
              tabs={locationFormTabs}
              activeTab={locationFormTab}
              onTabChange={setLocationFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingRecord ? p('edit_location') : p('create_location')}
            />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="location-form" onSubmit={handleSubmit}>
              {locationFormTab === 'basicInfo' && (
              <div className={INVENTAIRE_FORM_GRID}><div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_location_name')}</label>
                            <input type="text" required value={formData.location_name} onChange={(e) => setFormData({ ...formData, location_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_location_name')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_zone_type_field')}</label>
                            <CustomSelect
                              value={formData.zone_type}
                              onChange={(value) => setFormData({ ...formData, zone_type: value })}
                              options={[
                                { value: 'picking', label: p('zone_picking') },
                                { value: 'bulk', label: p('zone_bulk') },
                                { value: 'staging', label: p('zone_staging') },
                                { value: 'cold', label: p('zone_cold') },
                                { value: 'hazardous', label: p('zone_hazardous') }
                              ]}
                              placeholder={p('placeholder_select_zone')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_location_type_field')}</label>
                            <CustomSelect
                              value={formData.location_type}
                              onChange={(value) => setFormData({ ...formData, location_type: value })}
                              options={[
                                { value: 'zone', label: p('type_zone') },
                                { value: 'aisle', label: p('type_aisle') },
                                { value: 'shelf', label: p('type_shelf') },
                                { value: 'bin', label: p('type_bin') }
                              ]}
                              placeholder={p('placeholder_select_type')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_warehouse_field')}</label>
                            <CustomSelect
                              value={formData.warehouse_id}
                              onChange={(value) => setFormData({ ...formData, warehouse_id: value })}
                              options={warehouses.map(w => ({
                                value: w.id,
                                label: `${w.name || w.warehouse_name} (${w.code || '—'})`
                              }))}
                              placeholder={p('placeholder_select_warehouse')}
                              searchable={true}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status_field')}</label>
                            <CustomSelect
                              value={formData.status}
                              onChange={(value) => setFormData({ ...formData, status: value })}
                              options={statusOptions.map(s => ({ value: s.value, label: s.label, icon: <s.icon size={16} /> }))}
                              placeholder={p('placeholder_select_status')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_parent_location')}</label>
                            <CustomSelect
                              value={formData.parent_location_id || ''}
                              onChange={(value) => setFormData({ ...formData, parent_location_id: value === '' ? null : value })}
                              options={[
                                { value: '', label: p('no_parent_root') },
                                ...locations
                                  .filter(loc => !editingRecord || loc.id !== editingRecord.id)
                                  .map(loc => ({
                                    value: loc.id.toString(),
                                    label: `${loc.location_code} - ${loc.location_name}`
                                  }))
                              ]}
                              placeholder={p('placeholder_select_parent')}
                            />
                          </div>
                          <div className="flex items-center md:col-span-3">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.temperature_controlled}
                                onChange={(e) => setFormData({ ...formData, temperature_controlled: e.target.checked })}
                                className="w-5 h-5 text-blue-600 bg-white/10 border-white/20 rounded focus:ring-blue-500 focus:ring-2"
                              />
                              <span className="ml-3 text-white font-medium">{p('temperature_controlled_checkbox')}</span>
                            </label>
                          </div></div>
              )}

              {locationFormTab === 'capacityInfo' && (
              <div className={INVENTAIRE_FORM_GRID}><div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_capacity_volume')}</label>
                            <CustomNumberInput
                              value={formData.capacity_volume}
                              onChange={(value) => setFormData({ ...formData, capacity_volume: value })}
                              placeholder="0"
                              step={0.1}
                              min={0}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_capacity_weight')}</label>
                            <CustomNumberInput
                              value={formData.capacity_weight}
                              onChange={(value) => setFormData({ ...formData, capacity_weight: value })}
                              placeholder="0"
                              step={1}
                              min={0}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_current_occupancy_pct')}</label>
                            <CustomNumberInput
                              value={formData.current_occupancy}
                              onChange={(value) => setFormData({ ...formData, current_occupancy: value })}
                              placeholder="0"
                              step={1}
                              min={0}
                              max={100}
                            />
                          </div>
                          <div className="md:col-span-3">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('section_notes')}</label>
                            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={4} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_notes')} />
                          </div></div>
              )}
              </form>
            </div>
        </InventaireModalPortal>
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default EmplacementsZonesPage;

